import { spawn } from "node:child_process";
import { homedir } from "node:os";
import { join } from "node:path";
import process from "node:process";
import { daemonRuntimeRootPath } from "../../config/daemon-state";
import {
  resolveWakeLoopEnvPreset,
  WAKELOOP_HOME_ENV_KEY,
  type WakeLoopEnvPreset,
  type WakeLoopPathResolutionInput,
} from "../../config/paths";
import {
  daemonServiceExecutableWrapperPaths,
  daemonServiceLauncherPath,
} from "./daemon-service-bundle";
import { resolveDaemonServiceIdentity } from "./daemon-service-identity";

export type DaemonOwnedProcess = {
  pid: number;
  parentPid: number | null;
  command: string;
  mode: "foreground" | "background" | null;
  ownership: "stable_launcher" | "legacy_detached" | "cli_direct" | "unknown";
};

type ListedProcess = {
  pid: number;
  parentPid: number | null;
  command: string;
};

const POSIX_PROCESS_LINE_PATTERN = /^(\d+)\s+(\d+)\s+(.+)$/;
const WAKELOOP_PACKAGE_INSTALL_ENTRY_SEGMENT =
  "/node_modules/wakeloop/dist/wakeloop.js";
const WAKELOOP_DEV_LOCAL_ENTRY_SEGMENT = "/wakeloop-dev.js";
const ALL_WAKELOOP_ENV_PRESETS = [
  "prod",
  "dev-local",
  "canary",
] as const satisfies readonly WakeLoopEnvPreset[];
const DAEMON_BIN_COMMAND_PATTERN_BY_PRESET = {
  prod: buildWakeLoopBinCommandPatternForPreset("prod"),
  "dev-local": buildWakeLoopBinCommandPatternForPreset("dev-local"),
  canary: buildWakeLoopBinCommandPatternForPreset("canary"),
} as const satisfies Record<WakeLoopEnvPreset, RegExp>;

export async function listDaemonOwnedProcesses(
  overrides: {
    listProcesses?: () => Promise<ListedProcess[]>;
    ignoreProcessIds?: Iterable<number>;
    pathInput?: WakeLoopPathResolutionInput;
  } = {}
): Promise<DaemonOwnedProcess[]> {
  const listProcesses =
    overrides.listProcesses ?? listProcessesForCurrentPlatform;
  const ignoredProcessIds = new Set(
    Array.from(overrides.ignoreProcessIds ?? []).filter(
      (pid): pid is number => Number.isInteger(pid) && pid > 0
    )
  );
  const processes = await listProcesses();
  const ownedProcesses = processes
    .map((processInfo) =>
      classifyDaemonOwnedProcess(
        processInfo,
        ignoredProcessIds,
        overrides.pathInput
      )
    )
    .filter(
      (processInfo): processInfo is DaemonOwnedProcess => processInfo !== null
    );
  return collapseLauncherWrapperProcesses(ownedProcesses);
}

function classifyDaemonOwnedProcess(
  processInfo: ListedProcess,
  ignoredProcessIds: ReadonlySet<number>,
  pathInput: WakeLoopPathResolutionInput = {}
): DaemonOwnedProcess | null {
  if (ignoredProcessIds.has(processInfo.pid)) {
    return null;
  }
  const normalizedCommand = normalizeProcessCommand(processInfo.command);
  const runtimeRoot = normalizeProcessCommand(daemonRuntimeRootPath(pathInput));
  const launcherPath = normalizeProcessCommand(
    daemonServiceLauncherPath(pathInput)
  );
  const wrapperPaths = daemonServiceExecutableWrapperPaths(pathInput).map(
    normalizeProcessCommand
  );
  if (
    !looksLikeCurrentLaneDaemonCandidate({
      launcherPath,
      normalizedCommand,
      runtimeRoot,
      wrapperPaths,
    })
  ) {
    return null;
  }
  if (
    resolveDaemonCommandLaneScope({
      launcherPath,
      normalizedCommand,
      pathInput,
      runtimeRoot,
    }) !== "current_lane"
  ) {
    return null;
  }
  const ownership = resolveDaemonProcessOwnership(
    normalizedCommand,
    launcherPath,
    wrapperPaths
  );
  const mode = resolveDaemonProcessMode(normalizedCommand);
  return {
    pid: processInfo.pid,
    parentPid: processInfo.parentPid,
    command: processInfo.command,
    mode,
    ownership,
  };
}

async function listProcessesForCurrentPlatform(): Promise<ListedProcess[]> {
  if (process.platform === "win32") {
    return await listWindowsProcesses();
  }
  return await listPosixProcesses();
}

async function listPosixProcesses(): Promise<ListedProcess[]> {
  const output = await runCommand("ps", ["-axo", "pid=,ppid=,command="]);
  const processes: ListedProcess[] = [];
  for (const line of output.split("\n")) {
    const trimmed = line.trim();
    if (trimmed.length === 0) {
      continue;
    }
    const match = trimmed.match(POSIX_PROCESS_LINE_PATTERN);
    if (!(match?.[1] && match[2] && match[3])) {
      continue;
    }
    processes.push({
      pid: Number(match[1]),
      parentPid: Number(match[2]),
      command: match[3],
    });
  }
  return processes;
}

function resolveDaemonProcessOwnership(
  normalizedCommand: string,
  launcherPath: string,
  wrapperPaths: readonly string[]
): DaemonOwnedProcess["ownership"] {
  if (
    normalizedCommand.includes(launcherPath) ||
    wrapperPaths.some((wrapperPath) => normalizedCommand.includes(wrapperPath))
  ) {
    return "stable_launcher";
  }
  if (normalizedCommand.includes("legacy-detached")) {
    return "legacy_detached";
  }
  if (normalizedCommand.includes("service run")) {
    return "cli_direct";
  }
  return "unknown";
}

function resolveDaemonProcessMode(
  normalizedCommand: string
): DaemonOwnedProcess["mode"] {
  if (normalizedCommand.includes("--mode foreground")) {
    return "foreground";
  }
  if (normalizedCommand.includes("--mode background")) {
    return "background";
  }
  return null;
}

function looksLikeCurrentLaneDaemonCandidate(input: {
  normalizedCommand: string;
  runtimeRoot: string;
  launcherPath: string;
  wrapperPaths: readonly string[];
}): boolean {
  return (
    input.normalizedCommand.includes("service run") ||
    input.normalizedCommand.includes(input.launcherPath) ||
    input.wrapperPaths.some((wrapperPath) =>
      input.normalizedCommand.includes(wrapperPath)
    ) ||
    input.normalizedCommand.includes("legacy-detached") ||
    input.normalizedCommand.includes(input.runtimeRoot)
  );
}

function resolveDaemonCommandLaneScope(input: {
  normalizedCommand: string;
  runtimeRoot: string;
  launcherPath: string;
  pathInput: WakeLoopPathResolutionInput;
}): "current_lane" | "other_lane" | "not_wakeloop" {
  const currentLane = resolveWakeLoopEnvPreset(input.pathInput);
  const managedLane = resolveManagedDaemonPathLane({
    normalizedCommand: input.normalizedCommand,
    pathInput: input.pathInput,
  });
  if (managedLane) {
    return managedLane === currentLane ? "current_lane" : "other_lane";
  }

  const detectedLane = resolveDaemonCliCommandLane(
    input.normalizedCommand,
    input.pathInput
  );
  if (!detectedLane) {
    return "not_wakeloop";
  }
  return detectedLane === currentLane ? "current_lane" : "other_lane";
}

function resolveManagedDaemonPathLane(input: {
  normalizedCommand: string;
  pathInput: WakeLoopPathResolutionInput;
}): WakeLoopEnvPreset | null {
  for (const preset of ALL_WAKELOOP_ENV_PRESETS) {
    const lanePathInput = resolveLanePathInput(input.pathInput, preset);
    const laneLauncherPath = normalizeProcessCommand(
      daemonServiceLauncherPath(lanePathInput)
    );
    const laneWrapperPaths = daemonServiceExecutableWrapperPaths(
      lanePathInput
    ).map(normalizeProcessCommand);
    const laneRuntimeRoot = normalizeProcessCommand(
      daemonRuntimeRootPath(lanePathInput)
    );
    if (
      input.normalizedCommand.includes(laneLauncherPath) ||
      laneWrapperPaths.some((wrapperPath) =>
        input.normalizedCommand.includes(wrapperPath)
      ) ||
      input.normalizedCommand.includes(laneRuntimeRoot)
    ) {
      return preset;
    }
  }
  return null;
}

function resolveDaemonCliCommandLane(
  normalizedCommand: string,
  pathInput: WakeLoopPathResolutionInput
): WakeLoopEnvPreset | null {
  if (!normalizedCommand.includes("service run")) {
    return null;
  }
  if (matchesDaemonCliLane(normalizedCommand, "dev-local", pathInput)) {
    return "dev-local";
  }
  if (matchesDaemonCliLane(normalizedCommand, "canary", pathInput)) {
    return "canary";
  }
  if (matchesDaemonCliLane(normalizedCommand, "prod", pathInput)) {
    return "prod";
  }
  return null;
}

function matchesDaemonCliLane(
  normalizedCommand: string,
  preset: WakeLoopEnvPreset,
  pathInput: WakeLoopPathResolutionInput
): boolean {
  if (DAEMON_BIN_COMMAND_PATTERN_BY_PRESET[preset].test(normalizedCommand)) {
    return true;
  }

  if (preset === "dev-local") {
    return normalizedCommand.includes(WAKELOOP_DEV_LOCAL_ENTRY_SEGMENT);
  }

  const canaryPackageInstallRoot = resolveCanaryPackageInstallRoot(pathInput);
  if (preset === "canary") {
    return (
      normalizedCommand.includes(canaryPackageInstallRoot) ||
      normalizedCommand.includes(
        "/.local/share/wakeloop-canary/package-install/"
      )
    );
  }

  return (
    normalizedCommand.includes(WAKELOOP_PACKAGE_INSTALL_ENTRY_SEGMENT) &&
    !normalizedCommand.includes(canaryPackageInstallRoot) &&
    !normalizedCommand.includes(
      "/.local/share/wakeloop-canary/package-install/"
    ) &&
    !normalizedCommand.includes(WAKELOOP_DEV_LOCAL_ENTRY_SEGMENT)
  );
}

function collapseLauncherWrapperProcesses(
  processes: DaemonOwnedProcess[]
): DaemonOwnedProcess[] {
  const launcherPidSet = new Set<number>();

  for (const processInfo of processes) {
    if (processInfo.ownership === "stable_launcher") {
      launcherPidSet.add(processInfo.pid);
    }
  }
  if (launcherPidSet.size === 0) {
    return processes;
  }

  const collapsedProcesses: DaemonOwnedProcess[] = [];
  for (const processInfo of processes) {
    if (processInfo.ownership === "stable_launcher") {
      const hasManagedChild = processes.some(
        (candidate) =>
          candidate.pid !== processInfo.pid &&
          candidate.parentPid === processInfo.pid &&
          candidate.ownership !== "stable_launcher"
      );
      if (hasManagedChild) {
        continue;
      }
      collapsedProcesses.push(processInfo);
      continue;
    }

    if (
      processInfo.parentPid !== null &&
      launcherPidSet.has(processInfo.parentPid)
    ) {
      collapsedProcesses.push({
        ...processInfo,
        ownership:
          processInfo.ownership === "unknown"
            ? processInfo.ownership
            : "stable_launcher",
      });
      continue;
    }

    collapsedProcesses.push(processInfo);
  }

  return collapsedProcesses;
}

async function listWindowsProcesses(): Promise<ListedProcess[]> {
  const script =
    "Get-CimInstance Win32_Process | Select-Object ProcessId,ParentProcessId,CommandLine | ConvertTo-Json -Compress";
  const output = await runCommand("powershell", [
    "-NoProfile",
    "-Command",
    script,
  ]);
  const parsed = JSON.parse(output) as
    | Array<{
        ProcessId?: number;
        ParentProcessId?: number;
        CommandLine?: string | null;
      }>
    | {
        ProcessId?: number;
        ParentProcessId?: number;
        CommandLine?: string | null;
      };
  const rows = Array.isArray(parsed) ? parsed : [parsed];
  const processes: ListedProcess[] = [];
  for (const row of rows) {
    if (
      typeof row.ProcessId !== "number" ||
      typeof row.ParentProcessId !== "number" ||
      typeof row.CommandLine !== "string" ||
      row.CommandLine.trim().length === 0
    ) {
      continue;
    }
    processes.push({
      pid: row.ProcessId,
      parentPid: row.ParentProcessId,
      command: row.CommandLine,
    });
  }
  return processes;
}

async function runCommand(command: string, args: string[]): Promise<string> {
  return await new Promise((resolve, reject) => {
    const child = spawn(command, args, {
      stdio: ["ignore", "pipe", "pipe"],
      env: process.env,
      windowsHide: true,
    });
    let stdout = "";
    let stderr = "";
    child.stdout?.on("data", (chunk) => {
      stdout += chunk.toString();
    });
    child.stderr?.on("data", (chunk) => {
      stderr += chunk.toString();
    });
    child.once("error", reject);
    child.once("close", (code) => {
      if (code !== 0) {
        reject(
          new Error(
            `process inventory command failed: ${command} ${args.join(" ")} ${stderr.trim()}`
          )
        );
        return;
      }
      resolve(stdout);
    });
  });
}

function buildWakeLoopBinCommandPatternForPreset(
  preset: WakeLoopEnvPreset
): RegExp {
  const aliases = resolveDaemonServiceIdentity({
    env: {
      WAKELOOP_ENV_PRESET: preset,
    },
  }).cliEntryAliases.map(escapeRegex);
  return new RegExp(
    `(^|[\\\\s\\\\/])(${aliases.join("|")})(?:\\\\.cmd|\\\\.exe|\\\\.js)?(?=\\\\s|$)`,
    "u"
  );
}

function escapeRegex(value: string): string {
  return value.replaceAll(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function resolveCanaryPackageInstallRoot(
  pathInput: WakeLoopPathResolutionInput = {}
): string {
  return normalizeProcessCommand(
    join(
      pathInput.homeDir ?? homedir(),
      ".local",
      "share",
      "wakeloop-canary",
      "package-install"
    )
  );
}

function resolveLanePathInput(
  pathInput: WakeLoopPathResolutionInput,
  preset: WakeLoopEnvPreset
): WakeLoopPathResolutionInput {
  const currentPreset = resolveWakeLoopEnvPreset(pathInput);
  const env: NodeJS.ProcessEnv = {
    ...(pathInput.env ?? process.env),
    WAKELOOP_ENV_PRESET: preset,
  };
  if (preset !== currentPreset && env[WAKELOOP_HOME_ENV_KEY]) {
    delete env[WAKELOOP_HOME_ENV_KEY];
  }
  return {
    ...pathInput,
    env,
  };
}

function normalizeProcessCommand(command: string): string {
  return command.trim().toLowerCase().replaceAll("\\", "/");
}
